import { Clock, PenLine, Smartphone, ThumbsUp } from 'lucide-react';
import { AccentCard, AccentStat } from '@/shared/ui/accent-card';
import { plural, type SummaryCounts } from './logic';

/**
 * The page's three questions as four cards: what is still waiting on a
 * dentist, what came back and how, and what the patients are doing.
 *
 * Footnotes only appear when they are non-zero - "0 expire within 7 days" is a
 * line to read that says nothing.
 */
export function SummaryCards({ counts }: { counts: SummaryCounts | null }) {
  // While loading the numbers read "—" rather than a misleading 0.
  const value = (n: number | undefined) => (counts ? n : '—');

  return (
    <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
      <AccentCard icon={<Clock size={16} />} tone="caution" bar="caution" title="Awaiting review">
        <AccentStat
          value={value(counts?.awaiting)}
          deltaTone="caution"
          delta={
            counts?.expiringSoon
              ? `${plural(counts.expiringSoon, 'expires', 'expire')} within 7 days`
              : undefined
          }
        />
      </AccentCard>

      <AccentCard
        icon={<PenLine size={16} />}
        tone="negative"
        bar="negative"
        title="Changes requested"
      >
        <AccentStat value={value(counts?.changesRequested)} />
      </AccentCard>

      <AccentCard icon={<ThumbsUp size={16} />} tone="positive" bar="positive" title="Approved">
        <AccentStat
          value={value(counts?.approved)}
          deltaTone="positive"
          delta={
            counts?.approvedRecently
              ? `${String(counts.approvedRecently)} in the last 30 days`
              : undefined
          }
        />
      </AccentCard>

      <AccentCard icon={<Smartphone size={16} />} title="Patient links open">
        <AccentStat
          value={value(counts?.patientLinksOpen)}
          deltaTone="caution"
          delta={
            counts?.wantsAnotherTime
              ? `${plural(counts.wantsAnotherTime, 'patient wants', 'patients want')} another time`
              : undefined
          }
        />
      </AccentCard>
    </div>
  );
}
